import { Key, ClipboardCheck, Wrench, Phone, Banknote, ShieldCheck } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

const SERVICES = (t) => [
    {
        icon: Key,
        title: t({ de: "Mietersuche", en: "Tenant sourcing" }),
        text: t({
            de: "Wir finden den passenden Mieter für Ihre Wohnung – inkl. Bonitätsprüfung, Mietvertrag und Schlüsselübergabe.",
            en: "We find the right tenant – including credit check, rental contract and key handover.",
        }),
    },
    {
        icon: ClipboardCheck,
        title: t({ de: "Mietvertrag & Übergabe", en: "Contract & handover" }),
        text: t({
            de: "Rechtssichere Mietverträge nach aktuellem Standard. Strukturierte Übergabe inkl. Protokoll und Dokumentation.",
            en: "Legally sound rental contracts. Structured handover with full documentation.",
        }),
    },
    {
        icon: Wrench,
        title: t({ de: "Optionale Verwaltung", en: "Optional property management" }),
        text: t({
            de: "Auf Wunsch übernehmen wir die laufende Verwaltung: Mietzahlungen, Nebenkosten­abrechnung, Reparatur-Koordination, Mieterkommunikation.",
            en: "On request, we handle ongoing management: rent collection, utilities billing, repair coordination, tenant communication.",
        }),
    },
    {
        icon: Phone,
        title: t({ de: "Ein Ansprechpartner", en: "One point of contact" }),
        text: t({
            de: "Direkt vom Bauträger – persönlich, regional, mit kurzen Wegen. Sie haben einen festen Ansprechpartner für alles rund um Ihre Wohnung.",
            en: "Directly from the developer – personal, regional, short paths. One fixed contact for everything related to your apartment.",
        }),
    },
];

const ADVANTAGES = (t) => [
    {
        icon: Banknote,
        title: t({ de: "5 % AfA · steuerlich attraktiv", en: "5 % AfA · tax-efficient" }),
        text: t({
            de: "Degressive AfA für Neubau (§7 Abs. 5a EStG, Effizienzhaus 40, Baubeginn ab 1. Oktober 2023).",
            en: "Declining-balance AfA for new builds (§7 (5a) EStG, energy class 40, construction start from 1 Oct 2023).",
        }),
    },
    {
        icon: ShieldCheck,
        title: t({ de: "KfW-Förderung möglich", en: "KfW funding available" }),
        text: t({
            de: "Zinsvergünstigte Darlehen über KfW-Programme für klimafreundlichen Neubau.",
            en: "Subsidised loans via KfW climate-friendly new-build programmes.",
        }),
    },
];

export const RentalService = () => {
    const t = useT();
    const services = SERVICES(t);
    const advantages = ADVANTAGES(t);

    return (
        <section
            id="vermietung"
            className="bg-cream py-24 sm:py-32"
            data-testid="rental-service-section"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 mb-16">
                    <div className="lg:col-span-6">
                        <span className="overline">
                            {t({ de: "Vollservice für Anleger", en: "Full service for investors" })}
                        </span>
                        <h2 className="h-section mt-4 text-balance">
                            {t({ de: "Wir vermieten", en: "We rent it out" })}
                            <span className="italic text-brand-500"> {t({ de: "für Sie.", en: "for you." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        <p className="body-md">
                            {t({
                                de: "Sie wollen die Wohnung als Kapitalanlage erwerben und vermieten? Wir übernehmen das gerne komplett – von der Mietersuche bis zur optionalen laufenden Verwaltung. Direkt vom Bauträger, ohne Zwischenmakler.",
                                en: "You want to buy the apartment as an investment and rent it out? We're happy to take care of everything – from tenant sourcing to optional ongoing management. Directly from the developer, no intermediary broker.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
                    {services.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={i}
                                className="bg-cream p-8 lg:p-10 group transition-colors hover:bg-white"
                                data-testid={`rental-service-card-${i}`}
                            >
                                <div className="flex items-start gap-5">
                                    <div className="shrink-0 w-12 h-12 flex items-center justify-center bg-brand-50 text-brand-600 group-hover:bg-brand-500 group-hover:text-white transition-colors">
                                        <Icon size={22} strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <h3 className="font-heading text-xl text-ink mb-2">
                                            {s.title}
                                        </h3>
                                        <p className="font-body text-sm text-stone-600 leading-relaxed">
                                            {s.text}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-16">
                    <span className="overline">
                        {t({ de: "Steuerliche & finanzielle Vorteile", en: "Tax & financial benefits" })}
                    </span>
                    <div className="mt-5 grid md:grid-cols-2 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
                        {advantages.map((a, i) => {
                            const Icon = a.icon;
                            return (
                                <div
                                    key={i}
                                    className="bg-white p-7 lg:p-9 flex items-start gap-4"
                                    data-testid={`investor-advantage-${i}`}
                                >
                                    <div className="shrink-0 w-10 h-10 flex items-center justify-center bg-clay-50 text-brand-600">
                                        <Icon size={18} strokeWidth={1.6} />
                                    </div>
                                    <div>
                                        <h3 className="font-heading text-lg text-ink mb-1.5 leading-tight">
                                            {a.title}
                                        </h3>
                                        <p className="font-body text-sm text-stone-600 leading-relaxed">
                                            {a.text}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};
